import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "@tanstack/react-router";
import {
  MusicContext,
  type MusicContextValue,
  type MusicPlayOptions,
} from "./music-context";
import { MENU_TRACK } from "@/lib/configs/music";

const MUTE_STORAGE_KEY = "cfl:music-muted";
const TRACK_SRC_STORAGE_KEY = "cfl:music-track-src";
const TRACK_VOLUME_STORAGE_KEY = "cfl:music-track-volume";
const TRACK_LOOP_STORAGE_KEY = "cfl:music-track-loop";

type MusicProviderProps = {
  children: ReactNode;
  initialTrack?: MusicPlayOptions;
};

function getStoredTrack(): MusicPlayOptions | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const src = window.localStorage.getItem(TRACK_SRC_STORAGE_KEY);
    if (!src) {
      return null;
    }
    const volumeStr = window.localStorage.getItem(TRACK_VOLUME_STORAGE_KEY);
    const loopStr = window.localStorage.getItem(TRACK_LOOP_STORAGE_KEY);
    return {
      src,
      volume: volumeStr ? parseFloat(volumeStr) : undefined,
      loop: loopStr ? loopStr === "true" : undefined,
    };
  } catch {
    return null;
  }
}

function saveTrackToStorage(track: MusicPlayOptions | null) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (!track || !track.src) {
      window.localStorage.removeItem(TRACK_SRC_STORAGE_KEY);
      window.localStorage.removeItem(TRACK_VOLUME_STORAGE_KEY);
      window.localStorage.removeItem(TRACK_LOOP_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(TRACK_SRC_STORAGE_KEY, track.src);
    if (typeof track.volume === "number") {
      window.localStorage.setItem(TRACK_VOLUME_STORAGE_KEY, String(track.volume));
    } else {
      window.localStorage.removeItem(TRACK_VOLUME_STORAGE_KEY);
    }
    if (typeof track.loop === "boolean") {
      window.localStorage.setItem(TRACK_LOOP_STORAGE_KEY, String(track.loop));
    } else {
      window.localStorage.removeItem(TRACK_LOOP_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

function isGameRoute(pathname: string): boolean {
  // Routes that are NOT under (game) - music should not play here
  // Note: /home is allowed to have music
  const nonGameRoutes = [
    "/",
    "/arena",
    "/onboarding",
    "/landing-page",
    "/terms-of-service",
    "/about",
    "/ui-showcase",
  ];

  // Check if pathname matches any non-game route exactly or starts with it
  if (nonGameRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`))) {
    return false;
  }

  // All other routes (including /home) are considered game routes
  return true;
}

export function MusicProvider({ children, initialTrack }: MusicProviderProps) {
  const location = useLocation();
  const pathname = location.pathname;

  const storedMutePreference = (() => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      return window.localStorage.getItem(MUTE_STORAGE_KEY);
    } catch {
      return null;
    }
  })();

  const storedTrack = getStoredTrack();
  const initialMuted =
    storedMutePreference !== null ? storedMutePreference === "true" : true;

  // Only restore music if we're in a game route
  const isInGameRoute = isGameRoute(pathname);

  // Use initialTrack if provided, otherwise use stored track, otherwise fall back to MENU_TRACK
  // Tracks load regardless of mute state so audio starts immediately when user unmutes
  const effectiveInitialTrack =
    initialTrack ||
    (storedTrack && isInGameRoute
      ? { ...storedTrack, autoPlay: true }
      : null) ||
    (isInGameRoute ? { ...MENU_TRACK, autoPlay: true } : null);

  const initialVolume = effectiveInitialTrack?.volume ?? 0.5;
  const initialLoop = effectiveInitialTrack?.loop ?? true;
  const initialSrc = effectiveInitialTrack?.src ?? null;
  const initialTrackRequest =
    effectiveInitialTrack && effectiveInitialTrack.src
      ? { ...effectiveInitialTrack }
      : null;

  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const bufferRef = useRef<AudioBuffer | null>(null);
  const isStartingRef = useRef(false);
  const currentSrcRef = useRef<string | null>(initialSrc);
  const loopRef = useRef<boolean>(initialLoop);
  const resumeOnVisibilityRef = useRef<boolean>(false);
  const isMutedRef = useRef<boolean>(initialMuted);
  const volumeRef = useRef<number>(initialVolume);
  const hasStoredMutePreferenceRef = useRef<boolean>(
    storedMutePreference !== null,
  );
  const hasUserInteractedRef = useRef<boolean>(false);

  const [isReady, setIsReady] = useState(false);
  const [volume, setVolumeState] = useState(volumeRef.current);
  const [isMuted, setIsMuted] = useState(isMutedRef.current);
  const [currentSrc, setCurrentSrc] = useState<string | null>(initialSrc);
  const [trackRequest, setTrackRequest] = useState<MusicPlayOptions | null>(
    initialTrackRequest,
  );

  // Load MENU_TRACK when entering a game route with no active track
  // This handles navigating from non-game routes (e.g. /landing-page → /home)
  useEffect(() => {
    if (!isInGameRoute) return;
    if (currentSrcRef.current) return;
    setTrackRequest((prev) =>
      prev ? prev : { ...MENU_TRACK, autoPlay: true },
    );
  }, [isInGameRoute]);

  useEffect(() => {
    isMutedRef.current = isMuted;

    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(MUTE_STORAGE_KEY, isMuted ? "true" : "false");
      hasStoredMutePreferenceRef.current = true;
    } catch {
      // Ignore write failures (e.g. storage disabled)
    }
  }, [isMuted]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const stopInternal = useCallback(() => {
    const source = sourceNodeRef.current;
    if (!source) {
      return;
    }

    try {
      source.stop();
    } catch {
      // Swallow stop errors that happen if the source is already stopped.
    }
    source.disconnect();
    source.onended = null;
    sourceNodeRef.current = null;
  }, []);

  // Stop music when navigating outside of game routes
  useEffect(() => {
    if (!isInGameRoute) {
      // Stop music if we're not in a game route
      if (currentSrcRef.current) {
        stopInternal();
        bufferRef.current = null;
        setIsReady(false);
        setCurrentSrc(null);
        currentSrcRef.current = null;
        setTrackRequest(null);
      }
    }
  }, [pathname, isInGameRoute, stopInternal]);

  const ensureAudioGraph = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    const AudioContextClass =
      window.AudioContext ||
      (
        window as typeof window & {
          webkitAudioContext?: typeof AudioContext;
        }
      ).webkitAudioContext;

    if (!AudioContextClass) {
      console.warn("Web Audio API is not supported in this browser.");
      return null;
    }

    let context = audioContextRef.current;
    if (!context) {
      context = new AudioContextClass();
      audioContextRef.current = context;
    }

    let gainNode = gainNodeRef.current;
    if (!gainNode) {
      gainNode = context.createGain();
      gainNode.gain.value = isMutedRef.current ? 0 : volumeRef.current;
      gainNode.connect(context.destination);
      gainNodeRef.current = gainNode;
    }

    return { context, gainNode };
  }, []);

  const startPlayback = useCallback(async () => {
    const context = audioContextRef.current;
    const gainNode = gainNodeRef.current;
    const buffer = bufferRef.current;

    if (!context || !gainNode || !buffer) {
      return;
    }

    if (sourceNodeRef.current || isStartingRef.current) {
      return;
    }

    if (isMutedRef.current) {
      return;
    }

    isStartingRef.current = true;
    try {
      if (context.state === "suspended") {
        try {
          await context.resume();
        } catch (error) {
          console.error("Failed to resume audio context", error);
          isStartingRef.current = false;
          return;
        }
      }

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = loopRef.current;
      source.connect(gainNode);
      source.onended = () => {
        if (sourceNodeRef.current === source) {
          sourceNodeRef.current = null;
        }
      };
      source.start(0);
      sourceNodeRef.current = source;
    } catch (error) {
      console.error("Failed to start playback", error);
    } finally {
      isStartingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!trackRequest?.src) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const graph = ensureAudioGraph();
    if (!graph) {
      return;
    }

    const controller = new AbortController();
    let isCancelled = false;

    stopInternal();
    bufferRef.current = null;
    setIsReady(false);

    if (typeof trackRequest.volume === "number") {
      setVolumeState(trackRequest.volume);
    }

    loopRef.current = trackRequest.loop ?? true;
    currentSrcRef.current = trackRequest.src;
    setCurrentSrc(trackRequest.src);

    // Save track to localStorage for resume after refresh
    saveTrackToStorage({
      src: trackRequest.src,
      volume: trackRequest.volume,
      loop: trackRequest.loop,
    });

    const load = async () => {
      try {
        const response = await fetch(trackRequest.src, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(
            `Failed to load audio: ${response.status} ${response.statusText}`,
          );
        }

        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await graph.context.decodeAudioData(arrayBuffer);

        if (isCancelled) {
          return;
        }

        bufferRef.current = audioBuffer;
        setIsReady(true);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        console.error("Failed to initialize music loop", error);
      }
    };

    void load();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [trackRequest, ensureAudioGraph, stopInternal]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (isMuted) {
      return;
    }

    // Don't play music if we're not in a game route
    if (!isInGameRoute) {
      return;
    }

    // On mobile, we need user interaction before we can play
    // Only start playback if user has already interacted
    if (!hasUserInteractedRef.current) {
      return;
    }

    const context = audioContextRef.current;
    if (!context) {
      return;
    }

    // If context is suspended, try to resume it
    // Since user has interacted, we should be able to resume
    if (context.state === "suspended") {
      context
        .resume()
        .then(() => {
          // Retry playback after resume
          if (
            bufferRef.current &&
            !sourceNodeRef.current &&
            !isMutedRef.current &&
            hasUserInteractedRef.current
          ) {
            void startPlayback();
          }
        })
        .catch((error) => {
          console.error("Failed to resume audio context for playback", error);
        });
      return;
    }

    void startPlayback();
  }, [isReady, isMuted, isInGameRoute, startPlayback]);

  useEffect(() => {
    const gainNode = gainNodeRef.current;
    const context = audioContextRef.current;
    if (!gainNode || !context) {
      return;
    }

    const target = isMuted ? 0 : volume;
    gainNode.gain.setValueAtTime(target, context.currentTime);
  }, [isMuted, volume]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    // Resume AudioContext on first user interaction
    // On mobile (especially iOS Safari), audio must be started synchronously
    // within the user gesture handler, not in a promise callback
    const handleFirstInteraction = () => {
      hasUserInteractedRef.current = true;

      // Ensure audio context exists - create it if needed
      // On mobile, creating AudioContext during user gesture helps unlock audio
      const graph = ensureAudioGraph();
      if (!graph) {
        return;
      }

      const { context } = graph;

      // Resume context synchronously - this maintains the user gesture chain
      // This must be done synchronously for iOS Safari
      // We must resume during the gesture to unlock audio on mobile
      if (context.state === "suspended") {
        // Resume immediately - this is critical for iOS Safari
        // The promise will resolve asynchronously, but the resume is initiated during gesture
        // This unlocks the audio context for future playback
        void context.resume();
      }

      // Check if we're in a game route at the time of interaction
      const currentPathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      const isCurrentlyInGameRoute = isGameRoute(currentPathname);

      // Start playback synchronously within the gesture handler for mobile support
      // This must happen here, not in a promise callback, to work on iOS Safari
      // Try to start immediately if buffer is ready
      if (
        bufferRef.current &&
        !sourceNodeRef.current &&
        !isMutedRef.current &&
        isCurrentlyInGameRoute
      ) {
        // If buffer is ready, start playback now within the gesture
        // This is the ideal case for iOS Safari - everything happens synchronously
        void startPlayback();
      }
      // If buffer isn't ready yet, the isReady effect will start playback when ready
      // because we've now marked that user has interacted and context is unlocked
      // iOS Safari allows starting playback within a short window after gesture
    };

    document.addEventListener("click", handleFirstInteraction, { once: true, capture: true });
    document.addEventListener("touchstart", handleFirstInteraction, {
      once: true,
      capture: true,
    });
    document.addEventListener("keydown", handleFirstInteraction, { once: true, capture: true });

    const handleVisibilityChange = () => {
      const visibilityState = document.visibilityState;
      const context = audioContextRef.current;

      if (visibilityState === "hidden") {
        resumeOnVisibilityRef.current =
          !!bufferRef.current && !isMutedRef.current;

        stopInternal();

        if (context && context.state !== "closed") {
          context.suspend().catch(() => {});
        }
        return;
      }

      if (visibilityState !== "visible") {
        return;
      }

      if (!resumeOnVisibilityRef.current) {
        return;
      }

      const finalizeResume = () => {
        resumeOnVisibilityRef.current = false;
        if (
          !isMutedRef.current &&
          bufferRef.current &&
          !sourceNodeRef.current
        ) {
          void startPlayback();
        }
      };

      if (!context) {
        finalizeResume();
        return;
      }

      if (context.state === "suspended") {
        context
          .resume()
          .catch(() => {})
          .finally(finalizeResume);
        return;
      }

      finalizeResume();
    };

    const handlePageHide = () => {
      const context = audioContextRef.current;
      resumeOnVisibilityRef.current = false;
      stopInternal();
      if (context && context.state !== "closed") {
        context.suspend().catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", handlePageHide);
    }

    return () => {
      // Note: With { once: true }, listeners auto-remove after firing
      // These removals are only needed if component unmounts before interaction
      document.removeEventListener("click", handleFirstInteraction, { capture: true });
      document.removeEventListener("touchstart", handleFirstInteraction, { capture: true });
      document.removeEventListener("keydown", handleFirstInteraction, { capture: true });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", handlePageHide);
      }
    };
  }, [startPlayback, stopInternal, ensureAudioGraph]);

  useEffect(() => {
    return () => {
      stopInternal();

      const gain = gainNodeRef.current;
      if (gain) {
        gain.disconnect();
        gainNodeRef.current = null;
      }

      bufferRef.current = null;

      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context && context.state !== "closed") {
        context.close().catch(() => {});
      }
    };
  }, [stopInternal]);

  const play = useCallback(
    (options: MusicPlayOptions) => {
      if (!options.src) {
        return;
      }

      if (typeof options.volume === "number") {
        setVolumeState(options.volume);
        volumeRef.current = options.volume;
      }

      // Removed: autoPlay no longer force-unmutes — audio defaults to muted,
      // users opt-in via AudioControl toggle
      // if (
      //   options.autoPlay &&
      //   isMutedRef.current &&
      //   !hasStoredMutePreferenceRef.current
      // ) {
      //   isMutedRef.current = false;
      //   setIsMuted(false);
      // }

      loopRef.current = options.loop ?? true;

      if (currentSrcRef.current === options.src && bufferRef.current) {
        if (!isMutedRef.current && !sourceNodeRef.current) {
          void startPlayback();
        }
        return;
      }

      setTrackRequest({
        src: options.src,
        volume: options.volume,
        autoPlay: options.autoPlay,
        loop: options.loop,
      });
    },
    [startPlayback],
  );

  const stop = useCallback(() => {
    stopInternal();
    bufferRef.current = null;
    setIsReady(false);
    setCurrentSrc(null);
    currentSrcRef.current = null;
    setTrackRequest(null);
    // Clear stored track when explicitly stopped
    saveTrackToStorage(null);
  }, [stopInternal]);

  const mute = useCallback(() => {
    isMutedRef.current = true;
    setIsMuted(true);
  }, []);

  const unmute = useCallback(() => {
    isMutedRef.current = false;
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((previous) => {
      const next = !previous;
      isMutedRef.current = next;
      return next;
    });
  }, []);

  const setVolume = useCallback((value: number) => {
    setVolumeState(value);
    volumeRef.current = value;
  }, []);

  const value = useMemo<MusicContextValue>(
    () => ({
      isMuted,
      isReady,
      currentSrc,
      volume,
      play,
      stop,
      mute,
      unmute,
      toggleMute,
      setVolume,
    }),
    [
      currentSrc,
      isMuted,
      isReady,
      play,
      stop,
      mute,
      unmute,
      toggleMute,
      volume,
      setVolume,
    ],
  );

  return (
    <MusicContext.Provider value={value}>{children}</MusicContext.Provider>
  );
}

export type { MusicPlayOptions, MusicContextValue };
